import * as React from 'react';
import { Button, Form } from 'react-bootstrap';
import { getLocalization } from 'global/global';
import { useAppDispatch, useAppSelector } from 'index';
import { CampaignDraft } from 'Interfaces/Admin';
import { showAlert, showConfirm } from 'actions';
import { saveCampaign } from 'actions/adminCampaignActions';
import { adminGroupsSelector } from 'reducers/admin/selectors';
import { showGrid } from 'reducers/admin/ui';
import InfoButton from '../components/InfoButton';

interface Props {
  /** null when adding a new campaign. */
  campaignId: number | null;
}

const DEFAULT_MESSAGE = 'Dear Sir/Madam,\r\n\r\nWe are conducting a survey and your input would be '
  + 'appreciated. Click the button below to start the survey. Thank you for your participation!';

const EMPTY: CampaignDraft = {
  name: '',
  campaign_status: 'Open',
  campaign_message: DEFAULT_MESSAGE,
  invitedResponses: 0,
};

/** yyyy-mm-dd, the format the date inputs and the server both expect. */
const asDateInput = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  const pad = (n: number) => `${n < 10 ? '0' : ''}${n}`;
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** Replaces CampaignView.js. */
export default function CampaignForm(props: Props) {
  const dispatch = useAppDispatch();
  const groups = useAppSelector(adminGroupsSelector);
  const { forms, hierarchies } = useAppSelector((state) => state.admin.meta);
  const campaigns = useAppSelector((state) => state.admin.campaigns.collection);

  const existing = props.campaignId !== null
    ? campaigns.find((c) => c.id === props.campaignId)
    : undefined;

  const [draft, setDraft] = React.useState<CampaignDraft>(existing || EMPTY);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    setDraft(existing || { ...EMPTY });
    setCopied(false);
  }, [props.campaignId]);

  const set = <K extends keyof CampaignDraft>(field: K, value: CampaignDraft[K]) =>
    setDraft((d) => ({ ...d, [field]: value }));

  const fail = (message: string) => {
    dispatch(showAlert({ visible: true, message: getLocalization(message) }));
  };

  const save = () => {
    if (!draft.name) {
      return fail('campaignNameUnique');
    }
    if (!draft.formIdentifier) {
      return fail('campaignSelectFormlist');
    }
    if (!draft.campaign_start_date) {
      return fail('campaignStartDateInvalid');
    }
    if (!draft.campaign_end_date) {
      return fail('campaignEndDateInvalid');
    }
    if (new Date(draft.campaign_start_date) > new Date(draft.campaign_end_date)) {
      setDraft((d) => ({ ...d, campaign_start_date: null, campaign_end_date: null }));
      return fail('campaignInValidDateRanges');
    }
    if (draft.invitedResponses === null || Number(draft.invitedResponses) < 0) {
      return fail('campaignInValidTarget');
    }
    if (!draft.campaign_message) {
      return fail('campaignInvitationEmail');
    }
    // A form that collects a location needs a hierarchy to collect it from.
    const form = forms.find((f) => f.identifier === draft.formIdentifier);
    if (form?.includeLocationHierarchy && !draft.locationHierarchyId) {
      return fail('campaignSelectHierarchy');
    }

    void dispatch(saveCampaign(draft)).then((saved) => {
      if (saved) {
        dispatch(showGrid({ mode: 'campaigns', show: 'all' }));
      }
    });
    return undefined;
  };

  const copyUrl = () => {
    if (!draft.campaignUrl) {
      return;
    }
    void navigator.clipboard.writeText(draft.campaignUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <div className="admin-form p-3">
      <Form onSubmit={(e) => e.preventDefault()}>
        <Form.Group className="mb-2">
          <Form.Label>{getLocalization('campaignName')} <span className="text-danger">*</span></Form.Label>
          <Form.Control size="sm" value={draft.name || ''} onChange={(e) => set('name', e.target.value)} />
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label>
            {getLocalization('parentGroup')}
            <InfoButton message={getLocalization('campaignGroupInfo')} />
          </Form.Label>
          <Form.Control
            as="select"
            size="sm"
            value={draft.parentId ?? ''}
            onChange={(e) => set('parentId', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="" />
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </Form.Control>
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label>
            {getLocalization('locationHierarchy')}
            <InfoButton message={getLocalization('campaignLHInfo')} />
          </Form.Label>
          <Form.Control
            as="select"
            size="sm"
            value={draft.locationHierarchyId ?? ''}
            onChange={(e) => set('locationHierarchyId', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="" />
            {hierarchies.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
          </Form.Control>
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label>{getLocalization('campaignForm')}</Form.Label>
          <Form.Control
            as="select"
            size="sm"
            value={draft.formIdentifier ?? ''}
            onChange={(e) => set('formIdentifier', e.target.value || null)}
          >
            <option value="" />
            {forms.map((f) => <option key={f.identifier} value={f.identifier}>{f.name}</option>)}
          </Form.Control>
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label>{getLocalization('campaignStart')}</Form.Label>
          <Form.Control
            type="date"
            value={asDateInput(draft.campaign_start_date)}
            onChange={(e) => set('campaign_start_date', e.target.value || null)}
          />
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label>{getLocalization('campaignEnd')}</Form.Label>
          <Form.Control
            type="date"
            min={asDateInput(draft.campaign_start_date)}
            value={asDateInput(draft.campaign_end_date)}
            onChange={(e) => set('campaign_end_date', e.target.value || null)}
          />
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label>{getLocalization('campaignTarget')}</Form.Label>
          <Form.Control
            type="number"
            min={0}
            size="sm"
            value={draft.invitedResponses ?? 0}
            onChange={(e) => set('invitedResponses', Number(e.target.value))}
          />
          <Form.Text>{getLocalization('campaignTargetInfo')}</Form.Text>
        </Form.Group>

        <Form.Check
          id="campaign-invitees-only"
          label={getLocalization('inviteesOnly')}
          checked={!!draft.invitees_only}
          onChange={(e) => set('invitees_only', e.target.checked)}
        />
        <Form.Check
          id="campaign-one-response"
          label={getLocalization('oneResponse')}
          checked={!!draft.one_response_per_user}
          onChange={(e) => set('one_response_per_user', e.target.checked)}
        />

        <Form.Group className="mb-2">
          <Form.Label>{getLocalization('campaignUsers')}</Form.Label>
          <Form.Control
            as="textarea"
            rows={5}
            value={draft.campaignUsers || ''}
            onChange={(e) => set('campaignUsers', e.target.value)}
          />
          <Form.Text>{getLocalization('campaignUsersInfo')}</Form.Text>
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label>{getLocalization('campaignEmail')}</Form.Label>
          <Form.Control
            as="textarea"
            rows={5}
            value={draft.campaign_message || ''}
            onChange={(e) => set('campaign_message', e.target.value)}
          />
        </Form.Group>

        {existing && (
          <Form.Group className="mb-2">
            <Form.Label>
              {getLocalization('campaignUrl')}{' '}
              <i className="fa fa-copy pointer" role="button" onClick={copyUrl} />{' '}
              {copied && <span>{getLocalization('copiedToClipboard')}</span>}
            </Form.Label>
            <Form.Control size="sm" readOnly value={draft.campaignUrl || ''} />
          </Form.Group>
        )}

        <Form.Group className="mb-2">
          <Form.Label>{getLocalization('campaignStatus')}</Form.Label>
          <Form.Control
            as="select"
            size="sm"
            value={draft.campaign_status || 'Open'}
            onChange={(e) => set('campaign_status', e.target.value as 'Open' | 'Closed')}
          >
            <option value="Open">{getLocalization('open')}</option>
            <option value="Closed">{getLocalization('closed')}</option>
          </Form.Control>
        </Form.Group>
      </Form>

      <div className="form-actions">
        <Button size="sm" onClick={save}>
          {getLocalization(existing ? 'update' : 'save')}
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => dispatch(showConfirm({
            visible: true,
            message: getLocalization(existing ? 'confirmCancelEditCampaign' : 'confirmCancelNewCampaign'),
            onConfirm: () => { dispatch(showGrid({ mode: 'campaigns', show: 'all' })); },
          }))}
        >
          {getLocalization('cancel')}
        </Button>
      </div>
    </div>
  );
}
